import { graphql } from "@acto/gql";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { Customer, Kpi } from "@acto/taskpane/core/domain";
import { ClientFilter, ClientOrderBy, ClientSearchMode, ClientTrendFilter } from "@acto/gql/graphql";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { mapClientToCustomer } from "@acto/taskpane/core/data-access/customers/mapClientToCustomer";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";

const ClientsByKpiQuery = graphql(`
  query GetTenantClientsByKpi(
    $first: Int!
    $after: String
    $filter: ClientFilter
    $dateRange: DateRangeInput!
    $comparisonMode: KpiComparisonMode!
  ) {
    clients(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        node {
          ...ClientForDetailsWithRecommendedObservation
          revenue(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
          }
          profit(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
          }
          orderCount(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
          }
          margin(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
          }
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

// The clients list ranks and displays by the selected KPI. Marge is profit-backed
// (the row shows profit and its margin), so it sorts by profit.
export function orderByForKpi(kpi: Kpi): ClientOrderBy {
  switch (kpi) {
    case Kpi.PROFIT:
    case Kpi.MARGIN:
      return ClientOrderBy.ProfitDesc;
    case Kpi.ORDERS:
      return ClientOrderBy.OrdersDesc;
    default:
      return ClientOrderBy.RevenueDesc;
  }
}

interface Options {
  limit?: number;
  selectedKpi?: Kpi;
  dateRange: { startDate: string; endDate: string };
  searchTerm?: string;
  trends?: ClientTrendFilter[];
  enabled?: boolean;
}

export function useTenantClientsByKpi({
  limit = 10,
  selectedKpi = Kpi.REVENUE,
  dateRange,
  searchTerm,
  trends,
  enabled = true,
}: Options): PaginatedDataAccessResult<Customer> {
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());
  const trimmedSearchTerm = searchTerm?.trim() ?? "";
  const activeSearchTerm = trimmedSearchTerm.length > 0 ? trimmedSearchTerm : null;

  const filter: ClientFilter = {
    orderBy: orderByForKpi(selectedKpi),
    searchMode: ClientSearchMode.Smart,
    dateRange,
    comparisonMode,
    ...(activeSearchTerm ? { searchTerm: activeSearchTerm } : {}),
    ...(trends?.length ? { trends } : {}),
  };

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: ClientsByKpiQuery,
    limit,
    variables: { filter, dateRange, comparisonMode },
    enabled,
    connection: (data) => data.clients,
  });

  const customers = data?.map((node) => mapClientToCustomer(node));

  return {
    data: customers,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
  };
}
